import {
    ValidationTrigger,
    ValidationErrorType,
    ValidatorType,
    type FormValidators,
    type PropertyPath
} from "@ngt-form/core";
import { useMemo } from "react";

import type { IModelValidationResponse } from "src/api/Tmd.dtos";
import type { TMDValidationError } from "src/types/TMDValidationError";
import convertModelValidationResultToFormValidationResult from "src/utilities/convertModelValidationResultToFormValidationResult";
import convertPascalToCamelCasePropertyPath from "src/utilities/convertPascalToCamelCasePropertyPath";

/**
 * Describes asynchronous model validation with its metadata context.
 */
type ModelValidateFn<
    TFormValue extends object = object,
    TMetadataContext extends object = object
> = (model: TFormValue, context: TMetadataContext) => Promise<IModelValidationResponse>;

/**
 * Prepends server-backed validation to caller-supplied form validators.
 *
 * Postconditions: Without a validation function, returns supplied validators directly or an empty
 * array. Server validation defaults to Change; request failures become a blocking
 * validation-unavailable error. Caller validators retain order after the server validator.
 */
const useTMDFormValidators = <
    TFormValue extends object = object,
    TFormMetadata extends object = object,
    TMetadataContext extends object = object
>(
    validateFn?: ModelValidateFn<TFormValue, TMetadataContext> | null | undefined,
    validationTrigger?: ValidationTrigger | null | undefined,
    context?: TMetadataContext | null | undefined,
    validators?: FormValidators<TFormValue, TMDValidationError, TFormMetadata> | null | undefined
): FormValidators<TFormValue, TMDValidationError, TFormMetadata> => {
    return useMemo(
        () => {
            if (!validateFn && !validators) {
                return [];
            }

            if (!validateFn) {
                return validators;
            }

            return [
                {
                    type: ValidatorType.Form,
                    trigger: validationTrigger ?? ValidationTrigger.Change,
                    source: "TMD-Validate-Request",
                    subscription: {
                        value: true
                    },
                    /** Converts successful validation or returns a blocking error when the request fails. */
                    validate: async ({ formSnapshot: { value } }) => {
                        let response: IModelValidationResponse;
                        try {
                            response = await validateFn(value, context);
                        } catch {
                            return {
                                form: [
                                    {
                                        code: "TMD-VALIDATION-UNAVAILABLE",
                                        type: ValidationErrorType.Medium,
                                        message: "Validation could not be completed. Please try again.",
                                        meta: { frontEndError: true, blocking: true }
                                    }
                                ],
                                fields: {}
                            };
                        }

                        return convertModelValidationResultToFormValidationResult(response?.validation);
                    }
                },
                ...(validators ?? [])
            ];
        },
        [validateFn, validators, validationTrigger, context]
    );
};

export default useTMDFormValidators;
